import {
  AccountBalance,
  AllInbox,
  Assessment,
  Chat,
  ContactPage,
  DateRange,
  Discount,
  Dvr,
  Egg,
  Inventory,
  LocalShipping,
  MenuBook,
  Money,
  Monitor,
  PeopleAlt,
  RecentActors,
  Restaurant,
  SetMeal,
  Storefront,
  SvgIconComponent,
  TableBar,
  TableRestaurant,
  Widgets,
} from '@mui/icons-material';
import { FC, PropsWithChildren } from 'react';
import { AllergenName, DishUnit } from '../apiTypes';
import BookingsBadge from '../components/BookingsBadge';
import ReviewsBadge from '../components/ReviewsBadge';
import { CredentialKey } from './credentials';
import { t } from './translations';

export type MenuType = {
  group?: keyof typeof t;
  items: MenuItemType[];
}[];

export type MenuItemType = {
  name: keyof typeof t;
  icon: SvgIconComponent;
  route: string;
  credentialKey?: CredentialKey;
  badge?: FC<PropsWithChildren>;
  plan?: 'plus' | 'pro';
};

export const menu: MenuType = [
  {
    items: [
      {
        name: 'pos_terminal',
        icon: Monitor,
        route: 'pos',
        credentialKey: 'pos_terminal',
      },
      {
        name: 'kitchen_screen',
        icon: Dvr,
        route: 'kitchen',
        credentialKey: 'kitchen_screen',
        plan: 'plus',
      },
      {
        name: 'booking',
        icon: TableBar,
        route: 'booking',
        credentialKey: 'booking',
        badge: BookingsBadge,
        plan: 'plus',
      },
      {
        name: 'dashboard',
        icon: Widgets,
        route: '/',
        credentialKey: 'dashboard',
      },
      {
        name: 'order_archive',
        icon: Inventory,
        route: 'orderArchive',
        credentialKey: 'order_archive',
        plan: 'plus',
      },
      {
        name: 'pos_shift_archive',
        icon: Inventory,
        route: 'shiftsArchive',
        credentialKey: 'pos_shift_archive',
        plan: 'plus',
      },
      {
        name: 'visitor_reviews',
        icon: Chat,
        route: 'reviews',
        credentialKey: 'visitor_reviews',
        badge: ReviewsBadge,
      },
      {
        name: 'reports',
        icon: Assessment,
        route: 'reports',
        credentialKey: 'reports',
        plan: 'plus',
      },
    ],
  },
  {
    group: 'products',
    items: [
      {
        name: 'stores',
        icon: AllInbox,
        route: 'stores',
        credentialKey: 'stores',
        plan: 'pro',
      },
      {
        name: 'supply',
        icon: LocalShipping,
        route: 'supply',
        credentialKey: 'stores',
        plan: 'pro',
      },
    ],
  },
  {
    group: 'staff',
    items: [
      {
        name: 'journal',
        icon: DateRange,
        route: 'journal',
        credentialKey: 'journal',
        plan: 'pro',
      },
      {
        name: 'workers',
        icon: PeopleAlt,
        route: 'workers',
        credentialKey: 'workers',
      },
    ],
  },
  {
    group: 'finances',
    items: [
      {
        name: 'wallets',
        icon: AccountBalance,
        route: 'wallets',
        credentialKey: 'wallets',
      },
      {
        name: 'salary',
        icon: Money,
        route: 'salary',
        credentialKey: 'salary',
        plan: 'pro',
      },
    ],
  },
  {
    group: 'marketing',
    items: [
      {
        name: 'promotions',
        icon: Discount,
        route: 'promotions',
        credentialKey: 'promotions',
        plan: 'pro',
      },
      {
        name: 'clients',
        icon: RecentActors,
        route: 'clients',
        credentialKey: 'clients',
        plan: 'pro',
      },
    ],
  },
  {
    group: 'settings',
    items: [
      {
        name: 'products',
        icon: Egg,
        route: 'products',
        credentialKey: 'menu_and_product_settings',
        plan: 'pro',
      },
      {
        name: 'semifinished_products',
        icon: SetMeal,
        route: 'semifinished',
        credentialKey: 'menu_and_product_settings',
        plan: 'pro',
      },
      {
        name: 'menu',
        icon: MenuBook,
        route: 'menu',
        credentialKey: 'menu_and_product_settings',
      },
      {
        name: 'restaurant',
        icon: Restaurant,
        route: 'restaurant',
        credentialKey: 'restaurant_settings',
      },
      {
        name: 'edit_table_map',
        icon: TableRestaurant,
        route: 'editTableMap',
        credentialKey: 'restaurant_settings',
      },
      {
        name: 'legal_entities',
        icon: ContactPage,
        route: 'legal_entities',
        credentialKey: 'legal_entities',
        plan: 'pro',
      },
      {
        name: 'self_order_points',
        icon: Storefront,
        route: 'self_order_points',
        credentialKey: 'self_order_points',
        plan: 'pro',
      },
    ],
  },
];

export const menuItems = menu.reduce((acc: MenuItemType[], group) => [...acc, ...group.items], []);

export const allergens: AllergenName[] = [
  'celery',
  'crustacean',
  'eggs',
  'fish',
  'gluten',
  'lupins',
  'milk',
  'mustard',
  'nuts',
  'peanuts',
  'sesame',
  'shellfish',
  'soya',
  'sulphite',
];

export const defaultAllergens: Record<AllergenName, boolean> = {
  celery: false,
  crustacean: false,
  eggs: false,
  fish: false,
  gluten: false,
  lupins: false,
  milk: false,
  mustard: false,
  nuts: false,
  peanuts: false,
  sesame: false,
  shellfish: false,
  soya: false,
  sulphite: false,
};

export const units: DishUnit[] = ['g', 'ml', 'pc'];
